import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorCode, FileError, useDropzone } from 'react-dropzone';

import { AppContext } from '../../providers/App';
import { Box } from '../../atoms/Box';
import { MediaUploaderDropPlace } from './_DropPlace/UploaderDropPlace';
import { buildChangeEventObject } from '../../utils/reactEvents';
import { IUploaderContext, IUploaderProps } from './models';
import { Portal } from '@src/core/Portal';
import { Zoom } from '../../atoms/Zoom';
import { Animation } from '@src/core/Animation';
import { MultiContainer } from './_MultiContainer/MultiContainer';
import { SingleContainer } from './_SingleContainer/SingleContainer';
import { UploaderButtonOuter } from './_ButtonOuter/UploaderButtonOuter';
import { IUploaderFile, UploaderController } from './UploaderController';
import { UploaderContext } from './context';

import { classnames } from '@src/utils/classnames';
import { getFileObjects } from './utils';
import { AnyType } from '@src/models/common';
import { Icon } from '../../atoms/Icon';
import { Text } from '@src/atoms/Text';

import { TooltipNew as Tooltip } from '@src/atoms/TooltipNew';
import { getUUID } from '@src/utils/uuid';

import styled from './Uploader.module.scss';

const UploaderComponent: FC<Omit<IUploaderProps, 'controller' | 'id'>> = ({
  isMulti,
  value,
  previewSize,
  previewMode = 'rectangle',
  greyscale = false,
  accept,
  showFileInfo = false,
  isScroll = false,
  containerRef = null,
  maxFiles = isMulti ? Infinity : 1,
  maxFileSizeMB = 50,
  showErrors = false,
  isDropPlaceGrey,
  isHidden,
  outerButtonText = 'Загрузить файлы',
  outerButtonIcon = undefined,
  className,
  style,
  isDropPlace = true,
  onChange,
  showDeleteButton = false,
  isNotUpload = false,
  disabled = false,
}) => {
  const { controller, id, uploadFile, uploaderTitle, uploaderTitleTooltip } = useContext(UploaderContext);
  const { isAuthZone } = useContext(AppContext);
  const [fileList, setFileList] = useState<IUploaderFile[]>(controller.fileList);
  const [focus, setFocus] = useState(false);
  const [openOuterDropPlace, setOpenOuterDropPlace] = useState(false);
  const [openZoom, setOpenZoom] = useState<number | null>(null);
  const [stretchRef, setStretchRef] = useState<HTMLElement | null>(containerRef);
  if (!isAuthZone) disabled = true;
  /** Счетчик загруженных файлов */
  const filesLoadedCount = useRef(maxFiles);

  const acceptedFormats = useMemo(() => {
    if (!accept) return [];

    return Object.values(accept)
      .flat()
      .map((str) => str.slice(1).toUpperCase());
  }, [accept]);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      setOpenOuterDropPlace(false);
      const filteredAcceptedFiles = acceptedFiles.slice(0, maxFiles);
      const fileObjects = getFileObjects(filteredAcceptedFiles);
      controller.addFile(fileObjects, isMulti);
      await Promise.all<IUploaderFile[]>(
        filteredAcceptedFiles.map((file) => {
          uploadFile(file, false, isNotUpload);
        }),
      );
    },
    [controller, isMulti, isNotUpload, maxFiles, uploadFile],
  );

  const dropzone = useDropzone({
    accept,
    disabled: disabled,
    onDrop,
    onDragLeave: () => {
      setOpenOuterDropPlace(false);
    },
    onDropAccepted: () => {
      setFocus(false);
      filesLoadedCount.current = maxFiles;
    },
    onDropRejected: () => {
      filesLoadedCount.current = maxFiles;
    },
    onFileDialogOpen: () => {
      setFocus(true);
      controller.setStateErrors(new Map());
    },
    onFileDialogCancel: () => {
      setFocus(false);
    },
    onDragEnter: () => {
      controller.setStateErrors(new Map());
    },
    validator: (file) => {
      /** Проверяем, что это именно файл для корректной валидации */
      if (!file.name) return null;

      const errors: Array<FileError> | null = [];

      if (filesLoadedCount.current <= 0) {
        errors.push({ code: ErrorCode.TooManyFiles, message: 'Превышен лимит загрузки файлов' });
      }
      filesLoadedCount.current = filesLoadedCount.current - 1;

      if (file.size > maxFileSizeMB * 1024 * 1024) {
        errors.push({ code: ErrorCode.FileTooLarge, message: 'Файл слишком большой' });
      }

      if (accept && !accept[file.type]) {
        errors.push({ code: ErrorCode.FileInvalidType, message: 'Не корректный тип файла' });
      }

      controller.setFileErrors({ fileName: file.name, errors: errors.map((e) => e.code) });
      return errors.length === 0 ? null : errors;
    },
  });

  useEffect(() => {
    containerRef && setStretchRef(containerRef);
  }, [containerRef]);

  useEffect(() => {
    if (value && Array.isArray(value)) {
      controller.setStateFiles(value as IUploaderFile[], true);
    }
  }, [controller, value]);

  useEffect(() => {
    controller.addFileListener([
      { listenerName: `_innerUploaderFileListener_${id}`, cb: setFileList },
      {
        listenerName: `_externalOnChangeListener_${id}`,
        cb: (value, isExternal) => {
          const currentValue = isMulti ? value : value[0];
          !isExternal && onChange?.(buildChangeEventObject(currentValue as unknown as string));
        },
      },
    ]);
  }, [controller, id, isMulti, onChange]);

  useEffect(() => {
    function handleStretch(e) {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();

      if (e.dataTransfer && e.dataTransfer.types.includes('Files')) {
        setOpenOuterDropPlace(true);
      }
    }

    if (stretchRef) {
      stretchRef.addEventListener('dragenter', handleStretch);

      return () => {
        stretchRef.removeEventListener('dragenter', handleStretch);
      };
    }
  }, [disabled, stretchRef]);

  /** Чистим инпут, чтобы можно было один и тот же файл загружать */
  if (dropzone.inputRef.current) {
    dropzone.inputRef.current.value = '';
  }

  const isZoom = typeof openZoom === 'number';
  const rootClasses = classnames(styled.root, className);
  //иконка с display: none нужны для подгрузки этой иконки для возможности использования ее в офлайн режиме
  return (
    <>
      <Icon name={'circularAttention'} style={{ display: 'none' }} />
      {isHidden && !openOuterDropPlace && (
        <UploaderButtonOuter
          dropzone={dropzone}
          outerButtonText={outerButtonText}
          outerButtonIcon={outerButtonIcon}
          disabled={disabled}
        />
      )}

      {!isHidden && (
        <Box column gap={8} fullWidth>
          {uploaderTitle && !isMulti && (
            <Box gap={4} alignItems={'center'}>
              <Text id="uploaterTitleText" color="basicGreySemiDark" size={'caption'}>
                {uploaderTitle}
              </Text>
              {uploaderTitleTooltip && (
                <Tooltip content={uploaderTitleTooltip}>
                  <Icon name="circularAttention" view="basicGreySemiDark" size="sm" />
                </Tooltip>
              )}
            </Box>
          )}
          <Box
            data-id={id}
            className={rootClasses}
            style={style}
            fullWidth
            gap={20}
            column
            ref={(ref) => {
              if (ref && !stretchRef) {
                setStretchRef(ref);
              }
            }}
          >
            {isMulti ? (
              <MultiContainer
                acceptedFiles={acceptedFormats.join(',')}
                maxFileSizeMB={maxFileSizeMB}
                maxFiles={maxFiles}
                dropzone={dropzone}
                isDropPlaceGrey={isDropPlaceGrey}
                value={fileList}
                onZoom={setOpenZoom}
                isScroll={isScroll}
                showErrors={showErrors}
                isDropPlace={isDropPlace}
                openOuterDropPlace={openOuterDropPlace}
                previewMode={previewMode}
                disabled={disabled}
                focus={focus}
                showDeleteButton={showDeleteButton}
              />
            ) : (
              <SingleContainer
                acceptedFiles={acceptedFormats.join(',')}
                maxFileSizeMB={maxFileSizeMB}
                maxFiles={maxFiles}
                dropzone={dropzone}
                previewMode={previewMode}
                previewSize={previewSize}
                isDropPlaceGrey={isDropPlaceGrey}
                greyscale={greyscale}
                showFileInfo={showFileInfo}
                showDeleteButton={showDeleteButton}
                disabled={disabled}
                focus={focus}
              />
            )}
            {isZoom && (
              <Zoom
                files={fileList}
                activeIndex={openZoom}
                open={isZoom}
                onClose={() => setOpenZoom(null)}
                isFileNameShown
              />
            )}
          </Box>
        </Box>
      )}
      {openOuterDropPlace && (
        <Portal container={stretchRef}>
          <Animation open={openOuterDropPlace}>
            <MediaUploaderDropPlace
              dropzone={dropzone}
              previewMode={previewMode}
              previewSize={previewSize}
              isStretched
              isOuterContainerRef={!!containerRef}
              disabled={disabled}
              focus={focus}
            />
          </Animation>
        </Portal>
      )}
    </>
  );
};

export const Uploader: FC<IUploaderProps> = ({ controller, ...innerProps }) => {
  const controllerRef = useRef(controller || new UploaderController({ fileList: innerProps.value as IUploaderFile[] }));
  const { ngApiClient } = useContext(AppContext);

  const value = useMemo<IUploaderContext>(
    () => ({
      controller: controllerRef.current,
      /** Math.random() - защита от дурака! Id очень важен т.к. используется как преффикс при добавлении имени слушателя, его отсутствие все сломает */
      id: innerProps.id || getUUID(),
      entityFieldId: innerProps.entityFieldId,
      entityId: innerProps.entityId,
      isMulti: innerProps.isMulti,
      uploaderTitle: innerProps.uploaderTitle,
      uploaderTitleTooltip: innerProps.uploaderTitleTooltip,
      uploadFile: async (file: AnyType, isRetry?: boolean, isNotUpload?: boolean) => {
        try {
          if (isRetry) {
            file.loading = true;
            file.loadingError = false;
          }
          controllerRef.current.setFileLoading({ fileName: file.name, loading: true });
          if (!isNotUpload) {
            await controllerRef.current.uploadFiles({
              ngApiClient,
              file,
              entityId: innerProps.entityId,
              entityFieldId: innerProps.entityFieldId,
              isRetry,
              isMulti: innerProps.isMulti,
            });
          }
          controllerRef.current.setFileLoading({ fileName: file.name, loading: false });
          return file;
        } catch (error) {
          controllerRef.current.mutateFile(null, file.uuid, innerProps.isMulti, true);
          return null;
        }
      },
      children: innerProps.children,
      multiItemExtention: innerProps.multiItemExtention,
    }),
    [
      innerProps.id,
      innerProps.entityFieldId,
      innerProps.entityId,
      innerProps.isMulti,
      innerProps.uploaderTitle,
      innerProps.uploaderTitleTooltip,
      innerProps.children,
      ngApiClient,
    ],
  );

  return (
    <UploaderContext.Provider value={value}>
      <UploaderComponent {...innerProps} />
    </UploaderContext.Provider>
  );
};
